/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * Licensed under The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */
import { StyledDatePickerButton } from '#PRIVATE_StyledDatePickerButton';
import { useTranslationBundle } from '#hooks/UNSAFE_useTranslationBundle';
import { useUser } from '#hooks/UNSAFE_useUser';
import { BundleType } from '#resources/nls/bundle';
import { getMonthName } from '#utils/PRIVATE_dayUtils';
import { IsoMonth } from '#utils/UNSAFE_calendarDateUtils';
import { ComponentProps, useMemo } from 'preact/compat';
import { useCellNavigation } from '../UNSAFE_DatePicker/useCellNavigation';
import { useFocusableCell } from '../UNSAFE_DatePicker/useFocusableCell';

export type UseMonthCellProps = {
  /**
   * Specifies if this month is today's month.
   */
  isCurrent: boolean;

  /**
   * Specifies if this month should be focused first when
   * one tabs into the MonthsGridView.
   */
  isFocusable: boolean;

  /**
   * Specifies if this month is currently focused.
   */
  isFocused: boolean;

  /**
   * Specifies if this month is currently selected.
   */
  isSelected: boolean;

  /**
   * The month represented by this MonthCell.
   */
  month: IsoMonth;

  /**
   * A callback that will be invoked when a selection action is performed on this MonthCell.
   * @param payload The payload containing the month represented by this cell.
   */
  onAction: (payload: { month: IsoMonth }) => void;

  /**
   * A callback that will be invoked when a navigation action is performed on this MonthCell.
   * @param payload The payload specifying the direction of the navigation performed and the month.
   */
  onNavigation: (payload: {
    direction: 'up' | 'next' | 'down' | 'previous';
    month: IsoMonth;
  }) => void;
};

/**
 * A custom hook that adds functionalities to the MonthCell component and computes properties for the
 * StyledDatePickerButton.
 */
export const useMonthCell = ({
  isCurrent,
  isFocusable,
  isFocused,
  isSelected,
  month,
  onAction,
  onNavigation
}: UseMonthCellProps) => {
  const { locale } = useUser();
  const monthText = useMemo(
    () => ({
      short: getMonthName(locale, month, '*', 'short') as string,
      long: getMonthName(locale, month, '*', 'long') as string
    }),
    [locale, month]
  );

  // custom aria label for selected and current month
  const translations = useTranslationBundle<BundleType>('@oracle/oraclejet-preact');
  const ariaLabel = `${monthText.long}${
    isCurrent ? `, ${translations.datePicker_currentDate()}` : ''
  }${isSelected ? `, ${translations.datePicker_selected()}` : ''}`;

  const buttonProps = useFocusableCell({ isFocusable, isFocused });
  const buttonEventHandlers = useCellNavigation({
    navigationMap: {
      ArrowDown: 'down',
      ArrowLeft: { ltr: 'previous', rtl: 'next' },
      ArrowRight: { ltr: 'next', rtl: 'previous' },
      ArrowUp: 'up'
    },
    payload: { month },
    onAction,
    onNavigation
  });

  return {
    buttonProps: {
      'aria-label': ariaLabel,
      isActivable: true,
      label: monthText.short,
      isHoverable: true,
      isSelected,
      isToday: isCurrent,
      variant: 'month' as const,
      ...buttonProps,
      ...buttonEventHandlers
    } as ComponentProps<typeof StyledDatePickerButton>
  };
};
